import { Button, Input, InputNumber, notification, Upload } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { debounce, isNumber } from 'lodash'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GLOBAL_CLIENT } from 'src/helpers/GlobalClient'
import { typeFund, UPLOAD_FILE_TYPE } from 'src/helpers/constants'
import { formatCurrency } from 'src/helpers/functions'
import { useStore } from 'src/hooks'
import { Request2 } from 'src/common/Request2'
import api from 'src/common/api'

const { Dragger } = Upload
const { TextArea } = Input

type TopupPingPongTabProps = {
   onClose: () => void
   exchangeRatePingpong: number
   minRechargePingpong?: number
}

const TopupPingPongTab: React.FC<TopupPingPongTabProps> = ({ onClose, exchangeRatePingpong, minRechargePingpong }) => {
   const FundStore = useStore('FundStore')
   const CommonConfigStore = useStore('CommonConfigStore')
   const { designerConfigList } = CommonConfigStore
   const { t } = useTranslation()

   const [amount, setAmount] = useState<number | undefined>(undefined)
   const [transactionId, setTransactionId] = useState('')
   const [note, setNote] = useState('')
   const [proofImage, setProofImage] = useState('')
   const [fileList, setFileList] = useState<any[]>([])
   const [submitting, setSubmitting] = useState(false)

   const minAmount = minRechargePingpong ? Number(minRechargePingpong) : 0
   const invalidAmount = !amount || !isNumber(amount) || amount <= 0 || (minAmount > 0 && amount < minAmount)

   const handleUpload = async (info: any) => {
      const { file } = info

      try {
         const result = await Request2.uploadFile(file, api.upload.uploadFile)
         if (result?.data) {
            setProofImage(result.data.filename || result.data)
            setFileList([file])
            notification.success({
               message: t(GLOBAL_CLIENT.thongbao),
               description: 'Upload ảnh chứng từ thành công'
            })
         }
      } catch (error) {
         notification.error({
            message: t(GLOBAL_CLIENT.thongbao),
            description: 'Upload ảnh thất bại'
         })
      }
   }

   const handleSubmit = async () => {
      if (invalidAmount) {
         const minMsg = minAmount > 0 ? ` (tối thiểu $${formatCurrency(minAmount)})` : ''
         notification.error({
            message: t(GLOBAL_CLIENT.thongbao),
            description: `Vui lòng nhập số tiền hợp lệ${minMsg}`
         })
         return
      }

      if (!transactionId.trim()) {
         notification.error({
            message: t(GLOBAL_CLIENT.thongbao),
            description: 'Vui lòng nhập mã giao dịch (Transaction ID)'
         })
         return
      }

      if (!proofImage) {
         notification.error({
            message: t(GLOBAL_CLIENT.thongbao),
            description: 'Vui lòng upload ảnh chứng từ'
         })
         return
      }

      setSubmitting(true)

      // User nhập USD, quy đổi ngược sang VND = USD × exchangeRatePingpong
      const priceUSD = amount
      const priceVND = amount * (exchangeRatePingpong || 1)
      const code = `PP${Date.now()}`

      const params = {
         price: priceUSD,
         priceVND,
         code,
         description: note + '(PingPong)' || 'PingPong topup',
         type: typeFund.RECHARGE,
         transactionId: transactionId.trim(),
         proofImage,
         paymentMethod: 'pingpong'
      }

      try {
         const result = await FundStore.createFund(params)
         if (result?.code === 200) {
            notification.success({
               message: t(GLOBAL_CLIENT.thongbao),
               description: 'Tạo lệnh nạp tiền PingPong thành công. Vui lòng chờ admin xác nhận.'
            })
            onClose()
         } else {
            notification.error({
               message: t(GLOBAL_CLIENT.thongbao),
               description: result?.message || 'Tạo lệnh nạp tiền thất bại'
            })
         }
      } catch (error) {
         notification.error({
            message: t(GLOBAL_CLIENT.thongbao),
            description: 'Tạo lệnh nạp tiền thất bại'
         })
      } finally {
         setSubmitting(false)
      }
   }

   const uploadProps = {
      accept: UPLOAD_FILE_TYPE,
      maxCount: 1,
      fileList,
      beforeUpload: (file: any) => {
         // Prevent auto upload, we handle it manually
         return false
      },
      onChange: (info: any) => {
         const { fileList: newFileList } = info
         setFileList(newFileList.slice(-1))

         if (info.file.status !== 'removed') {
            handleUpload({ file: info.file.originFileObj || info.file })
         }
      },
      onRemove: () => {
         setProofImage('')
         setFileList([])
      }
   }

   return (
      <div>
         {/* Info section */}
         <div
            style={{
               background: '#f6ffed',
               border: '1px solid #b7eb8f',
               borderRadius: 6,
               padding: '12px 16px',
               marginBottom: 20
            }}
         >
            <p style={{ fontWeight: 600, marginBottom: 8 }}>To complete your topup request:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
               <li>
                  Pingpong #1:{' '}
                  <a href="mailto:pp.billing.us@gmail.com" style={{ color: '#1890ff' }}>
                     {designerConfigList?.emailPingpong}
                  </a>
               </li>

               <li>Fill in the form below with your submission details.</li>
               <li>We will process your top up request as soon as possible.</li>
            </ul>
         </div>

         {/* Form */}
         <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
               <label style={{ fontWeight: 500 }}>
                  Amount (USD) <span style={{ color: 'red' }}>*</span>
               </label>
               <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="0.00"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                  min={0}
                  step={0.01}
                  onChange={(value: any) => setAmount(value)}
                  value={amount}
               />
               {amount && isNumber(amount) && amount > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                     {`Tỉ giá: ${formatCurrency(exchangeRatePingpong, true)}đ/USD  —  Quy đổi VND: ${formatCurrency(
                        amount * (exchangeRatePingpong || 1),
                        true
                     )}đ`}
                  </div>
               )}
               {minAmount > 0 && (
                  <div
                     style={{
                        marginTop: 4,
                        fontSize: 12,
                        fontStyle: 'italic',
                        color: invalidAmount ? 'red' : '#888'
                     }}
                  >
                     Số tiền nạp tối thiểu: ${formatCurrency(minAmount)}
                  </div>
               )}
            </div>
            <div style={{ flex: 1 }}>
               <label style={{ fontWeight: 500 }}>
                  Transaction ID <span style={{ color: 'red' }}>*</span>
               </label>
               <Input
                  style={{ marginTop: 4 }}
                  placeholder="Enter transaction ID"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  maxLength={100}
               />
            </div>
         </div>

         <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
               <label style={{ fontWeight: 500 }}>
                  Upload proof <span style={{ color: 'red' }}>*</span>
               </label>
               <div style={{ marginTop: 4 }}>
                  <Dragger {...uploadProps}>
                     <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                     </p>
                     <p className="ant-upload-text">Click or drop file to upload your proof</p>
                  </Dragger>
                  {proofImage && (
                     <div style={{ marginTop: 8, textAlign: 'center' }}>
                        <img
                           src={proofImage}
                           alt=""
                           role="presentation"
                           style={{ maxWidth: 150, maxHeight: 150, objectFit: 'contain' }}
                        />
                        <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Preview</p>
                     </div>
                  )}
               </div>
            </div>
            <div style={{ flex: 1 }}>
               <label style={{ fontWeight: 500 }}>Note (optional)</label>
               <TextArea
                  style={{ marginTop: 4 }}
                  placeholder="Note (optional)..."
                  rows={5}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
               />
            </div>
         </div>

         {/* Footer */}
         <div
            style={{
               display: 'flex',
               justifyContent: 'flex-end',
               gap: 12,
               marginTop: 24,
               borderTop: '1px solid #f0f0f0',
               paddingTop: 16
            }}
         >
            <Button onClick={onClose}>{t(GLOBAL_CLIENT.huy)}</Button>
            <Button
               type="primary"
               onClick={debounce(handleSubmit, 500)}
               disabled={invalidAmount || !transactionId.trim() || !proofImage}
               loading={submitting}
               style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
               Create
            </Button>
         </div>
      </div>
   )
}

export default TopupPingPongTab
